import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Eye, EyeOff, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";

const RULES = [
  { key: "len", label: "At least 12 characters",       test: (p) => p.length >= 12 },
  { key: "up",  label: "One uppercase letter",         test: (p) => /[A-Z]/.test(p) },
  { key: "lo",  label: "One lowercase letter",         test: (p) => /[a-z]/.test(p) },
  { key: "num", label: "One number",                   test: (p) => /[0-9]/.test(p) },
  { key: "sym", label: "One symbol (! @ # $ %…)",      test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const passed = RULES.filter((r) => r.test(pw)).length;
  const strong = passed === RULES.length;
  const matches = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing or invalid reset token"); return; }
    if (!strong) { toast.error("Password doesn't meet the policy yet"); return; }
    if (!matches) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. You can sign in now.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050B1F] text-slate-100 p-6" data-testid="reset-missing-token">
        <div className="max-w-md text-center">
          <XCircle className="h-10 w-10 mx-auto text-rose-400 mb-4" />
          <h1 className="font-display font-bold text-2xl text-white">Missing reset token.</h1>
          <p className="text-slate-400 text-[13.5px] mt-3 leading-relaxed">
            This link is incomplete. Reset links come from the email we sent — open the latest one and try again, or request a new one.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 text-[12.5px]">
            <Link to="/forgot-password" className="text-cyan-300 hover:text-cyan-200 font-medium underline">Request a new link</Link>
            <span className="text-slate-500">·</span>
            <Link to="/login" className="text-slate-300 hover:text-white font-medium">Back to sign-in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#050B1F]" data-testid="reset-page">
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-slate-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(45% 40% at 20% 10%, rgba(34,211,238,0.16), transparent 65%), radial-gradient(35% 30% at 90% 90%, rgba(16,185,129,0.12), transparent 65%)",
        }} />
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }} />
        <div className="relative flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 p-[1.5px]">
            <div className="h-full w-full rounded-[5px] bg-[#050B1F] flex items-center justify-center">
              <div className="h-1.5 w-1.5 rounded-full bg-rose-400" />
            </div>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[19px] tracking-tight leading-none text-white">RiskEye</div>
            <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-cyan-300 mt-0.5">by seQure</div>
          </div>
        </div>
        <div className="relative">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-cyan-300 mb-3">Set a new password</div>
          <h2 className="font-display font-bold text-4xl text-white leading-tight">Almost back in.<br/><span className="text-emerald-300">Just pick a good one.</span></h2>
          <p className="text-slate-400 text-[14px] mt-4 max-w-md leading-relaxed">
            Passwords are hashed with bcrypt (cost 12), never logged, and never stored in plaintext. Your TOTP secret and 10 recovery codes are unaffected — you'll still need your authenticator to complete sign-in.
          </p>
        </div>
        <div className="relative flex items-center gap-3 text-[10.5px] font-mono uppercase tracking-[0.22em] text-slate-500">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> bcrypt · cost 12</span>
        </div>
      </div>

      <div className="flex items-center justify-center bg-white p-8 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-900 mb-8 font-medium">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign-in
          </Link>

          <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.28em] font-mono text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1 mb-4">
            <KeyRound className="h-3.5 w-3.5" /> New password
          </div>
          <h1 className="font-display font-bold text-3xl text-slate-900 leading-tight">Set a new password.</h1>
          <p className="text-slate-500 text-[13.5px] mt-2 leading-relaxed">
            Choose a strong one — meets policy below and you're back in the register in a few clicks.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div>
              <Label htmlFor="pw" className="text-[12px] font-mono uppercase tracking-wider text-slate-500 mb-1.5 block">New password</Label>
              <div className="relative">
                <Input
                  id="pw"
                  type={showPw ? "text" : "password"}
                  required
                  autoFocus
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="h-11 pr-10"
                  data-testid="reset-password"
                />
                <button type="button" onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="reset-password-toggle">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="pw2" className="text-[12px] font-mono uppercase tracking-wider text-slate-500 mb-1.5 block">Confirm password</Label>
              <Input
                id="pw2"
                type={showPw ? "text" : "password"}
                required
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="h-11"
                data-testid="reset-password-confirm"
              />
              {pw2 && !matches && <div className="mt-1.5 text-[12px] text-rose-600 font-medium">Passwords don't match yet.</div>}
              {pw2 && matches && <div className="mt-1.5 text-[12px] text-emerald-600 font-medium inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Passwords match.</div>}
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="reset-rules">
              <div className="font-mono uppercase tracking-widest text-[10px] text-slate-400 mb-2">Password policy · {passed}/{RULES.length}</div>
              <div className="grid grid-cols-2 gap-y-1">
                {RULES.map((r) => {
                  const ok = r.test(pw);
                  return (
                    <div key={r.key} className={`text-[12px] inline-flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-slate-500"}`}>
                      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-3.5 w-3.5 rounded-full border border-slate-300 inline-block" />}
                      {r.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy || !strong || !matches}
              className="w-full h-11 bg-[#0A1329] hover:bg-[#162347] text-white font-medium text-[14px] tracking-tight transition-colors"
              data-testid="reset-submit"
            >
              {busy ? "Updating password…" : "Reset password →"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
